import React, { useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { api, apiErrorMessage } from "@/lib/api";
import { uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { UserAvatar, resolveAvatarUrl } from "@/components/UserAvatar";
import { Bell, Mail, Moon, Palette, ShieldCheck, Smartphone, Sun, Monitor, Camera, Check, LogOut, AlertTriangle, User, AtSign, Sparkles, EyeOff, Lock, Globe, Trash2, Save } from "lucide-react";
import DiscordLogo from "@/assets/discord-logo.jpg";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function getBrowserName() {
  if (typeof navigator === "undefined") return "Browser";
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  return "Browser";
}

function getDeviceName() {
  if (typeof navigator === "undefined") return "This Device";
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  if (/Linux/.test(ua)) return "Linux";
  return "This Device";
}

// ---------- shared section card ----------
function SectionCard({ id, icon: Icon, title, description, children, testId }) {
  return (
    <section
      id={id}
      data-testid={testId}
      className="rounded-2xl border border-zinc-200/80 bg-white/80 p-6 shadow-[0_1px_0_rgba(0,0,0,0.02),0_8px_24px_-12px_rgba(15,23,42,0.08)] backdrop-blur-sm transition-colors dark:border-white/[0.06] dark:bg-white/[0.025] dark:shadow-[0_1px_0_rgba(255,255,255,0.02),0_8px_24px_-12px_rgba(0,0,0,0.6)]"
    >
      <header className="mb-5 flex items-start gap-3">
        {Icon ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-white/[0.05] dark:text-zinc-300">
            <Icon size={16} />
          </span>
        ) : null}
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-tight text-zinc-900 dark:text-white">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-[12.5px] text-zinc-500 dark:text-zinc-400">{description}</p>
          ) : null}
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

// ---------- input row ----------
function FieldRow({ icon: Icon, label, children, testId }) {
  return (
    <div data-testid={testId} className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">
        {Icon ? <Icon size={13} className="opacity-70" /> : null}
        {label}
      </label>
      {children}
    </div>
  );
}

// ---------- theme option card ----------
function ThemeOption({ value, current, onSelect, icon: Icon, label, preview }) {
  const active = current === value;
  return (
    <button
      type="button"
      data-testid={`settings-theme-option-${value}`}
      onClick={() => onSelect(value)}
      className={[
        "group flex flex-col items-stretch gap-2 rounded-xl border p-2 text-left transition-all",
        active
          ? "border-[#c8ff00]/50 bg-[#c8ff00]/[0.08] shadow-[0_0_18px_-6px_rgba(200,255,0,0.45)] dark:border-[#c8ff00]/40 dark:bg-[#c8ff00]/[0.06]"
          : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/[0.06] dark:bg-white/[0.02] dark:hover:border-white/[0.12] dark:hover:bg-white/[0.04]",
      ].join(" ")}
    >
      <div
        className={[
          "relative aspect-[16/9] w-full overflow-hidden rounded-lg border",
          preview,
        ].join(" ")}
      >
        <div className="absolute right-1.5 top-1.5 h-1.5 w-8 rounded-full bg-current opacity-30" />
        <div className="absolute left-1.5 top-1.5 h-1.5 w-12 rounded-full bg-current opacity-40" />
        <div className="absolute bottom-1.5 left-1.5 h-3 w-3 rounded-full bg-[#c8ff00]/80" />
        {active ? (
          <span className="absolute right-1.5 bottom-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#c8ff00] text-black shadow-[0_0_12px_rgba(200,255,0,0.55)]">
            <Check size={12} strokeWidth={3} />
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2 px-1 pb-0.5">
        <Icon size={13} className="text-zinc-600 dark:text-zinc-400" />
        <span className="text-[12.5px] font-medium text-zinc-700 dark:text-zinc-200">{label}</span>
      </div>
    </button>
  );
}

// ---------- pref row ----------
function PrefRow({ icon: Icon, title, desc, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200/80 bg-white px-4 py-3 transition-colors hover:bg-zinc-50/70 dark:border-white/[0.05] dark:bg-white/[0.02] dark:hover:bg-white/[0.035]">
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-white/[0.05] dark:text-zinc-300">
          <Icon size={14} />
        </span>
        <div className="min-w-0">
          <div className="text-[13.5px] font-medium text-zinc-900 dark:text-white">{title}</div>
          <div className="mt-0.5 text-[12px] text-zinc-500 dark:text-zinc-400">{desc}</div>
        </div>
      </div>
      <Switch
        data-testid={testId}
        checked={Boolean(checked)}
        onCheckedChange={onChange}
        className="data-[state=checked]:bg-[#c8ff00] data-[state=checked]:shadow-[0_0_10px_-2px_rgba(200,255,0,0.55)]"
      />
    </div>
  );
}

// ---------- main page ----------
export function SettingsPage() {
  const { logout, refresh, me } = useAuth();
  const { theme, setTheme } = useTheme();

  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0); // 0=hidden, 1=warn, 2=confirm
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/settings/me");
        setSettings(res.data);
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  async function load() {
    setError(null);
    try {
      const res = await api.get("/settings/me");
      setSettings(res.data);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        username: settings.username || null,
        email: settings.email || null,
        avatar_url: settings.avatar_url || null,
        bio: settings.bio || null,
        banner_url: settings.banner_url || null,
        preferences: settings.preferences || {},
      };
      const res = await api.put("/settings/me", payload);
      setSettings(res.data);
      await refresh();
      toast({ title: "Settings saved", description: "Your changes were applied successfully." });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function uploadAvatar(file) {
    if (!file) return;
    setAvatarUploading(true);
    setError(null);
    try {
      const uploaded = await uploadFile(file, "avatar");
      const nextSettings = { ...(settings || {}), avatar_url: uploaded.file_url };
      setSettings(nextSettings);
      const saved = await api.put("/settings/me", {
        username: nextSettings.username || null,
        email: nextSettings.email || null,
        avatar_url: nextSettings.avatar_url || null,
        bio: nextSettings.bio || null,
        banner_url: nextSettings.banner_url || null,
        preferences: nextSettings.preferences || {},
      });
      setSettings(saved.data);
      await refresh();
      setAvatarFile(null);
      toast({ title: "Profile photo updated", description: "Your avatar is now applied everywhere." });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setAvatarUploading(false);
    }
  }

  async function uploadBanner(file) {
    if (!file) return;
    setBannerUploading(true);
    setError(null);
    try {
      const uploaded = await uploadFile(file, "banner");
      const nextSettings = { ...(settings || {}), banner_url: uploaded.file_url };
      setSettings(nextSettings);
      const saved = await api.put("/settings/me", {
        username: nextSettings.username || null,
        email: nextSettings.email || null,
        avatar_url: nextSettings.avatar_url || null,
        bio: nextSettings.bio || null,
        banner_url: nextSettings.banner_url || null,
        preferences: nextSettings.preferences || {},
      });
      setSettings(saved.data);
      await refresh();
      toast({ title: "Banner updated", description: "Your profile banner is now live." });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBannerUploading(false);
    }
  }

  function onPickAvatar(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    uploadAvatar(file);
  }

  const profileUser = settings ? {
    ...settings,
    discord_id: settings.discord_id || me?.discord_id,
    avatar_hash: me?.avatar_hash,
  } : me;

  const roles = settings?.roles?.length ? settings.roles : [settings?.role || me?.role || "clipper"];

  return (
    <div data-testid="settings-page" className="mx-auto w-full max-w-[1180px] space-y-6">
      {/* Header */}
      <header className="border-b border-zinc-200/80 pb-5 dark:border-white/[0.06]">
        <h1 data-testid="settings-title" className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-[28px]">
          Settings
        </h1>
        <p className="mt-1.5 text-[13.5px] text-zinc-500 dark:text-zinc-400">
          Manage your profile, preferences, and appearance
        </p>
      </header>

      {error ? (
        <div data-testid="settings-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      {/* LOGIN METHODS */}
      <SectionCard
        id="login"
        icon={Lock}
        title="Login Method"
        description="Connected sign-in providers"
        testId="settings-login"
      >
        <div className="space-y-2.5">
          {[
            { key: "email", label: "Email & Password", connected: !!settings?.email, value: settings?.email, Icon: AtSign, color: "text-zinc-700 dark:text-zinc-300" },
            { key: "discord", label: "Discord", connected: !!settings?.discord_id, value: settings?.discord_id ? `@${settings?.discord_username}` : null, Icon: null, image: DiscordLogo, color: "text-indigo-500" },
            { key: "google", label: "Google", connected: false, value: null, Icon: null, image: null, color: "text-rose-500" },
          ].map((m) => (
            <div
              key={m.key}
              data-testid={`settings-login-method-${m.key}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 dark:border-white/[0.06] dark:bg-white/[0.025]"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                {m.image ? (
                  <img src={m.image} alt="" className="h-5 w-5 shrink-0 rounded" />
                ) : m.Icon ? (
                  <m.Icon size={16} className={m.color + " shrink-0"} />
                ) : (
                  <span className={"inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[12px] font-bold " + m.color}>{m.label[0]}</span>
                )}
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold text-zinc-900 dark:text-white">{m.label}</div>
                  {m.value ? (
                    <div className="truncate text-[11.5px] text-zinc-500 dark:text-zinc-400">{m.value}</div>
                  ) : null}
                </div>
              </div>
              {m.connected ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700 dark:text-emerald-300">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.7)] animate-pulse" />
                  Connected
                </span>
              ) : (
                <span className="text-[11px] text-zinc-500 dark:text-zinc-400">Not connected</span>
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      {/* PRIVACY */}
      <SectionCard
        id="privacy"
        icon={EyeOff}
        title="Privacy"
        description="Control who can see your profile and performance"
        testId="settings-privacy"
      >
        <PrefRow
          icon={EyeOff}
          title="Privacy Mode"
          desc="Hide your profile, earnings and activity from leaderboards and public pages. Admins can still see your account."
          checked={settings?.preferences?.privacy_mode}
          onChange={(v) => setSettings((p) => ({ ...p, preferences: { ...(p.preferences || {}), privacy_mode: v } }))}
          testId="pref-privacy_mode-switch"
        />
        {settings?.preferences?.privacy_mode && (
          <div data-testid="privacy-mode-badge" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-500 dark:text-amber-300">
            <EyeOff size={13} /> Private Account Enabled
          </div>
        )}
      </SectionCard>

      {/* NOTIFICATIONS */}
      <SectionCard
        id="notifications"
        icon={Bell}
        title="Notifications"
        description="Choose what you'd like to hear about"
        testId="settings-preferences"
      >
        <div className="space-y-2.5">
          {[
            ["emailNotifications", Mail, "Email Notifications", "Receive updates about campaigns, earnings, and approvals"],
            ["pushNotifications", Smartphone, "Push Notifications", "Browser and device alerts"],
            ["campaignAlerts", Bell, "Campaign Alerts", "Notify me when new campaigns match my niches"],
            ["paymentUpdates", Mail, "Payment Updates", "Alert me when payouts are processed"],
            ["autoJoinCampaign", ShieldCheck, "Auto-Join Campaigns", "Automatically join eligible campaigns"],
          ].map(([key, Icon, title, desc]) => (
            <PrefRow
              key={key}
              icon={Icon}
              title={title}
              desc={desc}
              checked={settings?.preferences?.[key]}
              onChange={(v) => setSettings((p) => ({ ...p, preferences: { ...(p.preferences || {}), [key]: v } }))}
              testId={`pref-${key}-switch`}
            />
          ))}
        </div>
      </SectionCard>

      {/* ACCOUNT & SECURITY */}
      <SectionCard
        id="account"
        icon={ShieldCheck}
        title="Account & Security"
        description="Manage your session and account safety"
        testId="settings-account"
      >
        {/* Device Sessions */}
        <div data-testid="settings-device-sessions" className="mb-3">
          <div className="mb-2 flex items-center gap-2">
            <Smartphone size={13} className="text-zinc-500" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Device Sessions</span>
          </div>
          <div className="space-y-2">
            {[{
              key: "current",
              name: getDeviceName(),
              browser: getBrowserName(),
              lastActive: "Now",
              current: true,
            }].map((s) => (
              <div
                key={s.key}
                data-testid={`device-session-${s.key}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 dark:border-white/[0.06] dark:bg-white/[0.025]"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-white/[0.05] dark:text-zinc-300">
                    <Smartphone size={13} />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-semibold text-zinc-900 dark:text-white">{s.name}</div>
                    <div className="truncate text-[11.5px] text-zinc-500 dark:text-zinc-400">{s.browser} · Last Active: {s.lastActive}</div>
                  </div>
                </div>
                {s.current ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700 dark:text-emerald-300">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.7)] animate-pulse" />
                    Current
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200/80 bg-white px-4 py-3 dark:border-white/[0.05] dark:bg-white/[0.02]">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-white/[0.05] dark:text-zinc-300">
              <LogOut size={14} />
            </span>
            <div className="min-w-0">
              <div className="text-[13.5px] font-medium text-zinc-900 dark:text-white">Log out of this device</div>
              <div className="mt-0.5 text-[12px] text-zinc-500 dark:text-zinc-400">End your current session safely</div>
            </div>
          </div>
          <Button
            data-testid="settings-logout"
            variant="ghost"
            onClick={() => logout()}
            className="h-9 rounded-full px-4 text-rose-600 hover:bg-rose-500/10 dark:text-rose-400 dark:hover:bg-rose-500/10"
          >
            <LogOut size={14} className="mr-1.5" />
            Log out
          </Button>
        </div>

        <div className="mt-3 rounded-xl border border-rose-500/15 bg-rose-500/[0.04] px-4 py-3 dark:border-rose-500/20 dark:bg-rose-500/[0.05]">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400">
              <AlertTriangle size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold text-rose-700 dark:text-rose-300">Danger zone</div>
              <div className="mt-0.5 text-[12px] text-rose-600/80 dark:text-rose-300/70">
                Permanently delete your account, submissions, and earnings history. This cannot be undone.
              </div>
              <Button
                data-testid="settings-delete-account-btn"
                variant="ghost"
                onClick={() => setDeleteStep(1)}
                className="mt-2 h-8 rounded-full px-3 text-[12px] font-semibold text-rose-600 hover:bg-rose-500/10 dark:text-rose-400"
              >
                <Trash2 size={13} className="mr-1.5" />
                Delete account
              </Button>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Sticky Save Button */}
      <div
        data-testid="settings-save-bar"
        className="sticky bottom-3 z-30 flex items-center justify-end gap-2 rounded-2xl border border-zinc-200/80 bg-white/95 px-4 py-3 shadow-[0_12px_30px_-12px_rgba(15,23,42,0.18)] backdrop-blur dark:border-white/[0.06] dark:bg-[#0a0a0a]/95 dark:shadow-[0_12px_30px_-12px_rgba(0,0,0,0.7)]"
      >
        <span className="mr-auto text-[12px] text-zinc-500 dark:text-zinc-400">
          Manual save protects against accidental changes.
        </span>
        <Button
          data-testid="settings-save-btn"
          disabled={saving}
          onClick={save}
          className="h-9 rounded-full bg-[#c8ff00] px-5 text-[13px] font-bold text-black hover:bg-[#b8ef00] disabled:opacity-60"
        >
          <Save size={14} className="mr-1.5" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {/* Delete account — two-step confirmation */}
      <Dialog open={deleteStep > 0} onOpenChange={(open) => { if (!open) setDeleteStep(0); }}>
        <DialogContent data-testid="delete-account-dialog" className="sm:max-w-md">
          {deleteStep === 1 && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-rose-600 dark:text-rose-400">
                  <AlertTriangle size={18} /> Delete your account?
                </DialogTitle>
                <DialogDescription className="pt-2">
                  This will permanently remove your profile, all submitted clips, joined campaigns, favorites, and earnings history. <strong>This action cannot be undone.</strong>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
                <Button data-testid="delete-account-cancel-1" variant="ghost" onClick={() => setDeleteStep(0)}>Cancel</Button>
                <Button
                  data-testid="delete-account-next"
                  className="bg-rose-600 text-white hover:bg-rose-700"
                  onClick={() => setDeleteStep(2)}
                >
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}
          {deleteStep === 2 && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-rose-600 dark:text-rose-400">
                  <Trash2 size={18} /> Final confirmation
                </DialogTitle>
                <DialogDescription className="pt-2">
                  Type <code className="rounded bg-rose-500/15 px-1.5 py-0.5 font-mono text-rose-700 dark:text-rose-300">DELETE</code> below to permanently delete your account.
                </DialogDescription>
              </DialogHeader>
              <Input
                data-testid="delete-account-input"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="mt-2"
              />
              <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
                <Button data-testid="delete-account-cancel-2" variant="ghost" onClick={() => { setDeleteStep(0); setDeleteConfirm(""); }}>Cancel</Button>
                <Button
                  data-testid="delete-account-confirm"
                  disabled={deleteConfirm !== "DELETE" || deleting}
                  className="bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await api.delete("/settings/me");
                      toast({ title: "Account deleted", description: "Your account and data have been permanently removed." });
                      setTimeout(() => logout(), 600);
                    } catch (e) {
                      toast({ title: "Couldn't delete", description: apiErrorMessage(e), variant: "destructive" });
                      setDeleting(false);
                    }
                  }}
                >
                  {deleting ? "Deleting…" : "Delete permanently"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
